import DataView from '@/components/data-view/data-view';
import DeleteConfirmationModal from '@/components/modal/DeleteConfirmationModal';
import { TableConfig } from '@/components/table/types';
import {
  useDeleteLearningPath,
  useLearningPaths,
} from '@/hooks/queries/learning-paths';
import { LearningPath } from '@/types/learning-path';
import { Icon } from '@iconify/react';
import { Button, Card, Chip, Spinner, Tooltip } from '@nextui-org/react';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LearningPathCard } from './components/learning-path-card';

const LearningPathsPage = () => {
  const navigate = useNavigate();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { data: learningPaths, isLoading, refetch } = useLearningPaths();
  const [viewType, setViewType] = React.useState<'tile' | 'list'>('list');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const [selectedPathToDelete, setSelectedPathToDelete] = React.useState<
    number | null
  >(null);

  const deleteMutation = useDeleteLearningPath();

  const handleDelete = (id: number) => {
    setSelectedPathToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (selectedPathToDelete) {
      deleteMutation.mutate(selectedPathToDelete, {
        onSuccess: () => {
          // toast.success('Learning path deleted successfully');
          refetch();
          setIsDeleteModalOpen(false);
          setSelectedPathToDelete(null);
        },
        onError: () => {
          // toast.error('Failed to delete learning path');
          setIsDeleteModalOpen(false);
          setSelectedPathToDelete(null);
        },
      });
    }
  };

  const tableConfig: TableConfig = {
    columns: [
      { uid: 'name', name: 'Name', sortable: true },
      {
        uid: 'status',
        name: 'Status',
        renderCell: (item: LearningPath) => (
          <Chip
            size="sm"
            color={item.status === 'PUBLISHED' ? 'success' : 'default'}
            variant="flat"
          >
            {item.status}
          </Chip>
        ),
      },
      { uid: 'tag', name: 'Tag', sortable: true },
      {
        uid: 'agents',
        name: 'Agents',
        renderCell: (item: LearningPath) => (
          <span>{item.prompts?.length || 0}</span>
        ),
      },

      {
        uid: 'updated_at',
        name: 'Last Updated',
        renderCell: (item: LearningPath) => (
          <span>
            {new Date(item.updated_at).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </span>
        ),
      },
      {
        uid: 'actions',
        name: 'Actions',
        renderCell: (item: LearningPath) => (
          <div className="flex items-center gap-2">
            <Tooltip content="Edit">
              <Button
                isIconOnly
                variant="light"
                size="sm"
                onPress={() =>
                  navigate(`/${tenantSlug}/learning-paths/${item.id}/edit`)
                }
              >
                <Icon icon="lucide:edit" className="w-5 h-5" />
              </Button>
            </Tooltip>
            <Tooltip content="Delete">
              <Button
                isIconOnly
                variant="light"
                size="sm"
                color="danger"
                onPress={() => handleDelete(item.id)}
              >
                <Icon icon="lucide:trash-2" className="w-5 h-5" />
              </Button>
            </Tooltip>
          </div>
        ),
      },
    ],
    initialVisibleColumns: [
      'name',
      'status',
      'tag',
      'agents',
      'updated_at',
      'actions',
    ],
    searchable: false,
    searchPlaceholder: 'Search paths...',
  };

  const renderCard = (path: LearningPath) => (
    <LearningPathCard key={path.id} path={path} />
  );

  const actions = (
    <Button
      color="primary"
      startContent={<Icon icon="lucide:plus" />}
      onPress={() => navigate(`/${tenantSlug}/learning-paths/create`)}
    >
      Create Path
    </Button>
  );

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col">
      {isLoading ? (
        <div className="flex h-full min-h-0 flex-1 items-center justify-center">
          <Spinner size="lg" />
        </div>
      ) : !learningPaths?.results?.length ? (
        <div className="flex-1 flex items-center justify-center">
          <Card className="p-12 flex flex-col items-center justify-center text-center">
            <Icon icon="lucide:book" className="w-12 h-12 text-default-300" />
            <h3 className="text-xl font-semibold mt-4">
              No Learning Paths Yet
            </h3>
            <p className="text-default-500 mt-2 max-w-md">
              Create your first learning path to start organizing your agents
              into structured learning experiences.
            </p>
            <Button
              color="primary"
              className="mt-6"
              startContent={<Icon icon="lucide:plus" />}
              onPress={() => navigate(`/${tenantSlug}/learning-paths/create`)}
            >
              Create Path
            </Button>
          </Card>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col min-w-0">
          <DataView
            title="Learning Paths"
            actions={actions}
            data={learningPaths.results}
            tableConfig={tableConfig}
            viewType={viewType}
            onViewChange={setViewType}
            renderCard={renderCard}
            disableTileView
          />
          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={() => setIsDeleteModalOpen(false)}
            onConfirm={confirmDelete}
            title="Delete Learning Path"
            entityName="learning path"
            isLoading={deleteMutation.isPending}
          />
        </div>
      )}
    </div>
  );
};

export default LearningPathsPage;
